import React, { useCallback, useState } from "react";
import { useField } from "formik";
import { Flex, ErrorMessage, Text } from "@jsq/ds/core";
import { EntitySelector } from "../../../EntitySelector";
import { EntityBankAccountType } from "../../../EntitySelector/types";

interface FormEntitySelectorProps {
  arenaId?: string;
  selectedEntityName: string;
  onEntitySelect: (
    entityId: string,
    entityName: string,
    bankAccount?: EntityBankAccountType | null,
  ) => void;
  isFundAdmin: boolean;
  hasBankAccount: boolean;
  fieldName: string;
  label?: string;
}

/**
 * FormEntitySelector component that integrates EntitySelector with Formik
 *
 * This component wraps the EntitySelector to provide proper Formik integration
 * using the useField hook for direct form field manipulation.
 */
export const FormEntitySelector: React.FC<FormEntitySelectorProps> = ({
  arenaId,
  selectedEntityName,
  onEntitySelect,
  isFundAdmin,
  hasBankAccount,
  fieldName,
  label,
}) => {
  const [, meta, helpers] = useField(fieldName);
  const [hasSelectedEntity, setHasSelectedEntity] = useState(false);

  const handleEntitySelect = useCallback(
    (entityId: string, entityName: string, bankAccount?: EntityBankAccountType | null) => {
      void helpers.setValue(entityId);
      void helpers.setTouched(true);
      void helpers.setError(undefined);
      setHasSelectedEntity(true);
      onEntitySelect(entityId, entityName, bankAccount);
    },
    [helpers, onEntitySelect],
  );

  const hasError = Boolean(meta.touched && meta.error && !hasSelectedEntity);

  return (
    <Flex flexDirection="column" gap={8}>
      {label && <Text>{label}</Text>}
      <EntitySelector
        arenaId={arenaId}
        selectedEntityName={selectedEntityName}
        onEntitySelect={handleEntitySelect}
        isFundAdmin={isFundAdmin}
        hasBankAccount={hasBankAccount}
        withFidesPaymentsCapabilityOnly={true}
      />
      {hasError && <ErrorMessage> Investment entity is required</ErrorMessage>}
    </Flex>
  );
};
